
/* Apex Wellness — Universal Product Page v4.0
   File: apex-product-v4.js
   Owns:
   - Gallery/thumbnails
   - Image modal
   - Variant resolution
   - Price/compare-at price synchronization
   - Availability/add-to-cart state
   - Final CTA variant synchronization
   - Quantity controls
   - Progressive enhancement only
*/

(() => {
  const moneyFormat = (cents, currency = 'USD', locale = document.documentElement.lang || 'en-US') => {
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
      }).format((Number(cents) || 0) / 100);
    } catch (_) {
      return `$${((Number(cents) || 0) / 100).toFixed(2)}`;
    }
  };

  class ApexProductV4 {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId;
      this.variants = this.readVariants();
      this.currency = window.Shopify?.currency?.active || window.Shopify?.currency || 'USD';

      this.form = root.querySelector('[data-apex-product-form]');
      this.variantInput = root.querySelector('[data-apex-variant-id]');
      this.optionInputs = [...root.querySelectorAll('[data-apex-option-input]')];

      this.price = root.querySelector('[data-apex-price]');
      this.comparePrice = root.querySelector('[data-apex-compare-price]');
      this.addButton = root.querySelector('[data-apex-add]');
      this.addText = root.querySelector('[data-apex-add-text]');

      this.ctaForm = root.querySelector('[data-apex-cta-form]');
      this.ctaVariantInput = root.querySelector('[data-apex-cta-variant-id]');
      this.ctaPrice = root.querySelector('[data-apex-cta-price]');
      this.ctaCompare = root.querySelector('[data-apex-cta-compare]');
      this.ctaButton = root.querySelector('[data-apex-cta-submit]');
      this.ctaText = root.querySelector('[data-apex-cta-text]');

      this.qtyInput = root.querySelector('[data-apex-qty-input]');
      this.qtyMinus = root.querySelector('[data-apex-qty-minus]');
      this.qtyPlus = root.querySelector('[data-apex-qty-plus]');

      this.modal = root.querySelector('[data-apex-modal]');
      this.modalContent = root.querySelector('[data-apex-modal-content]');
      this.modalClose = root.querySelector('[data-apex-modal-close]');

      this.bindGallery();
      this.bindOptions();
      this.bindQuantity();
      this.bindModal();
      this.syncFromInitialVariant();
    }

    readVariants() {
      const node = this.root.querySelector('[data-apex-variants]');
      if (!node) return [];

      try {
        return JSON.parse(node.textContent || '[]');
      } catch (_) {
        return [];
      }
    }

    bindGallery() {
      this.root.addEventListener('click', (event) => {
        const thumb = event.target.closest('[data-apex-thumb]');
        if (thumb) {
          this.activateMedia(thumb.dataset.mediaId);
          return;
        }

        const zoom = event.target.closest('[data-apex-zoom]');
        if (zoom) {
          const img = zoom.querySelector('img');
          if (img) this.openImageModal(img);
        }
      });
    }

    activateMedia(mediaId) {
      const items = [...this.root.querySelectorAll('[data-apex-media]')];
      const thumbs = [...this.root.querySelectorAll('[data-apex-thumb]')];

      items.forEach((item) => {
        const active = String(item.dataset.mediaId) === String(mediaId);
        item.hidden = !active;
        item.classList.toggle('is-active', active);
      });

      thumbs.forEach((thumb) => {
        const active = String(thumb.dataset.mediaId) === String(mediaId);
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-current', active ? 'true' : 'false');
      });
    }

    bindOptions() {
      if (!this.optionInputs.length) return;

      this.optionInputs.forEach((input) => {
        input.addEventListener('change', () => {
          const variant = this.resolveSelectedVariant();
          if (variant) this.applyVariant(variant);
        });
      });
    }

    resolveSelectedVariant() {
      if (!this.variants.length) return null;

      const selected = [...this.root.querySelectorAll('[data-apex-option]')].map((fieldset) => {
        const checked = fieldset.querySelector('[data-apex-option-input]:checked');
        return checked ? checked.value : null;
      });

      return this.variants.find((variant) => {
        const options = variant.options || [];
        return selected.every((value, index) => value == null || options[index] === value);
      }) || null;
    }

    syncFromInitialVariant() {
      if (!this.variantInput) return;

      const currentId = String(this.variantInput.value || '');
      const variant = this.variants.find((item) => String(item.id) === currentId);

      if (variant) {
        this.applyVariant(variant, { updateUrl: false });
      }
    }

    applyVariant(variant, options = {}) {
      const { updateUrl = true } = options;

      if (this.variantInput) this.variantInput.value = variant.id;
      if (this.ctaVariantInput) this.ctaVariantInput.value = variant.id;

      this.updatePriceNode(this.price, this.comparePrice, variant);
      this.updatePriceNode(this.ctaPrice, this.ctaCompare, variant);
      this.updateAvailability(this.addButton, this.addText, variant, 'Add to cart');
      this.updateAvailability(this.ctaButton, this.ctaText, variant, this.ctaText?.dataset.defaultLabel || 'Add to cart');

      if (variant.featured_media?.id) {
        this.activateMedia(variant.featured_media.id);
      }

      if (updateUrl && window.history?.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }

      this.root.dispatchEvent(new CustomEvent('apex:variant-change', {
        bubbles: true,
        detail: { variant },
      }));
    }

    updatePriceNode(priceNode, compareNode, variant) {
      if (priceNode) {
        priceNode.textContent = moneyFormat(variant.price, this.currency);
      }

      if (compareNode) {
        const showCompare = Number(variant.compare_at_price) > Number(variant.price);
        compareNode.hidden = !showCompare;
        compareNode.textContent = showCompare
          ? moneyFormat(variant.compare_at_price, this.currency)
          : '';
      }
    }

    updateAvailability(button, textNode, variant, availableLabel) {
      if (!button) return;

      const available = Boolean(variant.available);
      button.disabled = !available;

      if (textNode) {
        textNode.textContent = available ? availableLabel : 'Sold out';
      }
    }

    bindQuantity() {
      if (!this.qtyInput) return;

      const normalize = () => {
        const next = Math.max(1, Number.parseInt(this.qtyInput.value || '1', 10) || 1);
        this.qtyInput.value = String(next);
      };

      this.qtyMinus?.addEventListener('click', () => {
        normalize();
        this.qtyInput.value = String(Math.max(1, Number(this.qtyInput.value) - 1));
      });

      this.qtyPlus?.addEventListener('click', () => {
        normalize();
        this.qtyInput.value = String(Number(this.qtyInput.value) + 1);
      });

      this.qtyInput.addEventListener('change', normalize);
      this.qtyInput.addEventListener('blur', normalize);
    }

    bindModal() {
      if (!this.modal) return;

      this.modalClose?.addEventListener('click', () => this.closeModal());

      this.modal.addEventListener('click', (event) => {
        if (event.target === this.modal) this.closeModal();
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.modal.open) {
          this.closeModal();
        }
      });
    }

    openImageModal(img) {
      if (!this.modal || !this.modalContent) return;

      const clone = img.cloneNode(true);
      const srcset = img.getAttribute('srcset');

      if (srcset) clone.setAttribute('srcset', srcset);
      clone.removeAttribute('loading');

      this.modalContent.replaceChildren(clone);

      if (typeof this.modal.showModal === 'function') {
        this.modal.showModal();
      } else {
        this.modal.setAttribute('open', '');
      }
    }

    closeModal() {
      if (!this.modal) return;

      if (typeof this.modal.close === 'function' && this.modal.open) {
        this.modal.close();
      } else {
        this.modal.removeAttribute('open');
      }

      if (this.modalContent) this.modalContent.replaceChildren();
    }
  }

  const init = (scope = document) => {
    scope.querySelectorAll('[data-apex-product-v4]').forEach((root) => {
      if (root.dataset.apexV4Initialized === 'true') return;

      root.dataset.apexV4Initialized = 'true';

      const density = root.closest('.shopify-section')?.querySelector('[data-apex-product-v4]')?.dataset.density;
      if (density) root.dataset.density = density;

      new ApexProductV4(root);
    });
  };

  document.addEventListener('DOMContentLoaded', () => init());

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
  });
})();
